import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from '../../store';
import * as fromProcess from '../../store/process/process.reducer';
import { combineLatest, filter, first, Subject } from 'rxjs';
import { ApiState } from '../../model/api-state.model';
import { CalculationToClientConfirmPayload } from '../../model/calculation-to-client-confirm.model';
import { PathPayload } from '../../model/path.model';
import { ProcessService, ProcessType } from '../../services/process.service';
import { FormsService } from '../../services/forms.service';

const MIN_SPLITS_REQUIRED = 1;

@Component({
  selector: 'app-page-conclusion-remote',
  templateUrl: './conclusion-remote.component.html',
  styleUrls: ['./conclusion-remote.component.scss'],
})
export class PageConclusionRemoteComponent implements OnInit, OnDestroy {
  processKind$ = this.store.select(fromProcess.selectProcessKind);
  selectedPackage$ = this.store.select(fromProcess.selectProcessPackageName);
  addons$ = this.store.select(fromProcess.selectProcessAddons);
  selectedAgentSection$ = this.store.select(fromProcess.selectAgentSection);
  calculationToClientConfirmState$ = this.store.select(fromProcess.selectCalculationToClientConfirmState);
  calculationToClientConfirmData$ = this.store.select(fromProcess.selectCalculationToClientConfirmData);
  calculationToClientConfirmError$ = this.store.select(fromProcess.selectCalculationToClientConfirmError);
  discountCode$ = this.store.select(fromProcess.selectOfferDiscountCode);
  selectProcessHashCode$ = this.store.select(fromProcess.selectProcessHashCode);
  sendBackOfficeData$ = this.store.select(fromProcess.selectSendBackOfficeData);

  ngDestroy$ = new Subject<void>();

  apiState = ApiState;

  constructor(private store: Store<AppState>, private formsService: FormsService, private processService: ProcessService) {}

  ngOnInit(): void {
    this.store.dispatch(fromProcess.actions.setStep({ step: 9 }));
    this.processService.onPageInit();

    this.checkSuccess();
  }

  ngOnDestroy(): void {
    this.ngDestroy$.next();
    this.ngDestroy$.complete();
  }

  makeRequest(): void {
    combineLatest([
      this.processKind$,
      this.selectedPackage$,
      this.addons$,
      this.selectedAgentSection$,
      this.discountCode$,
      this.sendBackOfficeData$,
      this.selectProcessHashCode$,
    ])
      .pipe(first())
      .subscribe(
        ([processKind, packageCodeName, selectedAddons, selectedAgentSection, discountCode, sendBackOfficeData, calculationHashCode]) => {
          let path: PathPayload;

          switch (processKind) {
            case ProcessType.TRIPABROAD:
              path = {
                tripAbroad: this.formsService.tripAbroadApi,
              };
              break;
            case ProcessType.TRAVELINPL:
              path = {
                travelInPL: this.formsService.travelInPlApi,
              };
              break;
            case ProcessType.RESIGNATION:
              path = {
                resignation: this.formsService.resignationApi,
              };
              break;
            default:
              throw new Error('Unknown process kind');
          }

          const payload: CalculationToClientConfirmPayload = {
            path,
            policyholder: this.formsService.policyholderApi,
            insureds: this.formsService.insuredsApi,
            backofficeSection: sendBackOfficeData ? this.formsService.backOfficeSectionApi : null,
            selectedOffert: {
              packageCodeName,
              addOns: selectedAddons ?? {},
            },
            ...this.processService.apiPayload,
            discountCode: {
              code: discountCode || '',
            },
            ...(!(selectedAgentSection.premiumPercentIncrease === undefined || selectedAgentSection.premiumPercentIncrease === null)
              ? {
                  agentSection: selectedAgentSection,
                }
              : {}),
            agreements: this.formsService.agreementsApi,
            hashCode: calculationHashCode,
            agentCommissionSplits:
              this.formsService.agentCommissionSplits.controls.splits.length > MIN_SPLITS_REQUIRED
                ? this.formsService.agentCommisionSplitsApi
                : null,
          };

          this.store.dispatch(fromProcess.actions.calculationToClientConfirmStart({ payload }));
        },
      );
  }

  private checkSuccess() {
    this.calculationToClientConfirmState$
      .pipe(
        first(),
        filter((state) => state !== ApiState.success),
      )
      .subscribe(() => this.makeRequest());

    this.calculationToClientConfirmState$
      .pipe(
        filter((state) => state === ApiState.success),
        first(),
      )
      .subscribe(() => this.store.dispatch(fromProcess.actions.setSuccess({ success: true })));
  }
}
